/**
 * ClientWelcome — Cockpit client premium (Sprint V2).
 * ───────────────────────────────────────────────────
 * Miroir de la fiche dossier agence côté client.
 * Mission unique : « Voici votre véhicule, voici où on en est,
 * voici la prochaine étape ».
 *
 * Structure (mobile-first, mêmes composants que le cockpit agence) :
 *   1. Top bar discret (logo agence + conseiller)
 *   2. Multi-dossiers strip (si le client en a plusieurs)
 *   3. Hero V2 (photo + nom + progression + barre 13 segments)
 *   4. NextStepCard (cerveau client — ce qui compte MAINTENANT)
 *   5. Dernière étape terminée (rassurance tangible)
 *   6. Onglets pill : Suivi / Messages / Documents
 *   7. Contenu de l'onglet actif
 *   8. Carte conseiller (contact rapide)
 *   9. Bandeau setup mot de passe (dismissible, si compte sans pw)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Lock, X, Check, LifeBuoy, ArrowUpRight, MessageCircle, LogOut,
} from "lucide-react";
import Logo from "@/components/Logo";
import {
  Card, SegmentedControl, MicroLabel, NextActionCard, Avatar, Button,
} from "@/components/wyvio-ui";
import ClientHeroV2 from "@/components/client-v2/ClientHeroV2";
import ClientSlotsPicker from "@/components/client-v2/ClientSlotsPicker";
import ClientTimeline from "@/components/client/ClientTimeline";
import ClientMessages from "@/components/client/ClientMessages";
import ClientDocuments from "@/components/client/ClientDocuments";
import { resolveClientNextStep, resolveLastCompleted } from "@/lib/clientNextStep";
import api from "@/lib/api";
import { normalizeClientDetail } from "@/lib/normalize";

const TABS = [
  { key: "suivi",     label: "Suivi" },
  { key: "messages",  label: "Messages" },
  { key: "documents", label: "Documents" },
];

const ClientWelcome = () => {
  const navigate = useNavigate();
  const { dossierId: urlDossierId } = useParams();

  const [vehicle, setVehicle] = useState(null);
  const [rawAgency, setRawAgency] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [savCases, setSavCases] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("suivi");

  const [hasPassword, setHasPassword] = useState(true);
  const [pwd, setPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const [pwdDismissed, setPwdDismissed] = useState(false);
  const [pwdSaved, setPwdSaved] = useState(false);

  const load = async (isMounted = { current: true }) => {
    setVehicle(null);
    setLoading(true);
    try {
      const d = urlDossierId
        ? await api.clientDossierById(urlDossierId)
        : await api.clientDossier();
      if (!isMounted.current) return;
      const v = normalizeClientDetail(d);
      setVehicle(v);
      setRawAgency(v.agency || null);
      setAdvisor(v.advisor || null);
      setHasPassword(Boolean(d?.has_password));
      try {
        sessionStorage.setItem("wyvio.client.lastDossierId", d.id);
      } catch { /* ignore */ }

      // Charges annexes silencieuses
      api.clientListSavCases().then((r) => setSavCases(r?.cases || [])).catch(() => {});
      api.clientGetDelivery().then((r) => setDelivery(r?.delivery || null)).catch(() => setDelivery(null));
      // Le paiement n'est pas exposé côté client (agency-only endpoint).
      // resolveClientNextStep gère l'absence proprement et tombe sur la
      // logique par étape (langage rassurant).

      try {
        const list = await api.clientDossiers();
        if (isMounted.current && list?.count > 1) {
          setSiblings(list.dossiers.filter((x) => x.id !== d.id));
        } else if (isMounted.current) {
          setSiblings([]);
        }
      } catch { /* silent */ }
    } catch (e) {
      if (!isMounted.current) return;
      if (e.status === 401) navigate("/client/login", { replace: true });
      else if (e.status === 403 || e.status === 404) navigate("/client/welcome", { replace: true });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    const mounted = { current: true };
    load(mounted);
    return () => { mounted.current = false; };
  }, [urlDossierId]);

  // ── Cerveau : détermine la prochaine étape à afficher ─────────────────
  const nextStep = useMemo(() => {
    if (!vehicle) return null;
    return resolveClientNextStep({
      vehicle,
      payment: null,
      delivery,
      actions: { openMessages: () => setActiveTab("messages") },
    });
  }, [vehicle, delivery]);

  const lastCompleted = useMemo(
    () => (vehicle ? resolveLastCompleted(vehicle) : null),
    [vehicle],
  );

  const handleSendMessage = async (text) => {
    if (!text?.trim()) return;
    await api.clientSendMessage(text.trim());
    await load({ current: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (!pwd || pwd.length < 8) return setPwdError("8 caractères minimum");
    if (!/[a-zA-Z]/.test(pwd)) return setPwdError("Au moins une lettre");
    if (!/[0-9]/.test(pwd)) return setPwdError("Au moins un chiffre");
    setPwdBusy(true);
    try {
      await api.clientSetPassword(pwd);
      setPwdSaved(true);
      setHasPassword(true);
    } catch (err) {
      setPwdError(err.message || "Erreur");
    } finally {
      setPwdBusy(false);
    }
  };

  const handleLogout = async () => {
    try { await api.clientLogout(); } catch { /* ignore */ }
    navigate("/client/login", { replace: true });
  };

  if (loading || !vehicle) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] dark:bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={22} strokeWidth={2} className="text-[#0A0A0A] dark:text-white animate-spin-slow" />
      </div>
    );
  }

  const readOnly = Boolean(vehicle.isFinished);
  const messagesCount = vehicle.messages?.length || 0;

  return (
    <div
      data-testid="page-client-welcome"
      data-readonly={readOnly}
      className="min-h-screen bg-[#F7F8FA] dark:bg-[#0A0A0A] pb-16"
    >
      {/* Top bar minimaliste */}
      <header className="max-w-md sm:max-w-2xl mx-auto px-5 sm:px-7 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo variant="full" size="xs" rounded="rounded-md" />
          {rawAgency?.name && (
            <>
              <span className="text-[#D1D5DB] dark:text-[#3A3A3A]">·</span>
              <span className="text-[12.5px] font-medium text-[#6B7280] dark:text-[#A3A3A3] truncate max-w-[140px]">
                {rawAgency.name}
              </span>
            </>
          )}
        </div>
        <button
          onClick={handleLogout}
          data-testid="client-logout"
          aria-label="Se déconnecter"
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-white dark:hover:bg-[#111111] transition-colors"
        >
          <LogOut size={14} strokeWidth={2} />
        </button>
      </header>

      <main className="max-w-md sm:max-w-2xl mx-auto px-5 sm:px-7 pt-2 wyvio-fade-in space-y-6">
        {/* SAV actif : bannière discrète (aucune mention "SAV") */}
        {savCases.filter((s) => !s.is_closed).map((s) => (
          <button
            key={s.id}
            onClick={() => navigate(`/client/suivi/${s.id}`)}
            data-testid={`client-sav-banner-${s.id}`}
            className="w-full flex items-center gap-3 rounded-3xl bg-white dark:bg-[#111111] border border-[#EEF2F5] dark:border-[#262626] p-4 hover:border-[#0A0A0A]/20 dark:hover:border-white/20 transition-colors text-left"
          >
            <div className="h-10 w-10 rounded-2xl bg-[#0A0A0A] text-[#C8FF2F] flex items-center justify-center shrink-0">
              <LifeBuoy size={16} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-semibold text-[#0A0A0A] dark:text-white">
                Votre suivi après-vente
              </p>
              <p className="mt-0.5 text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3]">
                Consultez l&apos;évolution de votre demande.
              </p>
            </div>
            <ArrowUpRight size={16} strokeWidth={2} className="text-[#9CA3AF]" />
          </button>
        ))}

        {/* Multi-dossiers */}
        {siblings.length > 0 && (
          <SiblingsStrip
            current={vehicle}
            siblings={siblings}
            onSelect={(id) => navigate(`/client/dossier/${id}`)}
          />
        )}

        {/* Hero véhicule */}
        <ClientHeroV2 vehicle={vehicle} agency={rawAgency} />

        {/* Cockpit : prochaine étape */}
        {nextStep && <NextActionCard {...nextStep} testId="client-next-step-card" />}

        {/* Choix du créneau — apparaît uniquement quand des propositions sont actives */}
        {delivery?.status === "proposed" && (
          <ClientSlotsPicker
            delivery={delivery}
            onConfirmed={async () => {
              try {
                const r = await api.clientGetDelivery();
                setDelivery(r?.delivery || null);
              } catch { /* silent */ }
              await load({ current: true });
            }}
          />
        )}

        {/* Dernière étape terminée — rassurance */}
        {lastCompleted && !vehicle.isFinished && (
          <LastCompletedCard step={lastCompleted} />
        )}

        {/* Onglets pill */}
        <div className="pt-2 flex justify-center">
          <SegmentedControl
            testId="client-tabs"
            value={activeTab}
            onChange={setActiveTab}
            options={TABS.map((t) => ({
              key: t.key,
              label:
                t.key === "messages" && messagesCount > 0
                  ? `${t.label} · ${messagesCount}`
                  : t.label,
            }))}
          />
        </div>

        {/* Contenu onglet */}
        <div data-testid={`client-tab-content-${activeTab}`}>
          {activeTab === "suivi" && (
            <Card className="p-6">
              <MicroLabel>Historique du parcours</MicroLabel>
              <div className="mt-4">
                <ClientTimeline steps={vehicle.steps || []} />
              </div>
            </Card>
          )}
          {activeTab === "messages" && (
            <ClientMessages
              messages={vehicle.messages}
              advisor={advisor}
              readOnly={readOnly}
              onSend={handleSendMessage}
            />
          )}
          {activeTab === "documents" && (
            <ClientDocuments documents={vehicle.documents || []} />
          )}
        </div>

        {/* Conseiller */}
        {advisor?.name && (
          <AdvisorCard
            advisor={advisor}
            fallbackName={vehicle.commercial}
            onMessage={readOnly ? null : () => setActiveTab("messages")}
          />
        )}

        {/* Mot de passe (dismiss-able) */}
        {(!hasPassword || pwdSaved) && !pwdDismissed && (
          <PasswordSetupCard
            pwd={pwd}
            setPwd={setPwd}
            busy={pwdBusy}
            error={pwdError}
            saved={pwdSaved}
            onSubmit={submitPassword}
            onDismiss={() => setPwdDismissed(true)}
          />
        )}

        <p className="pt-2 text-center text-[11.5px] text-[#9CA3AF] dark:text-[#525252]">
          Vous pouvez revenir à tout moment via ce lien.
        </p>
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────────

const SiblingsStrip = ({ current, siblings, onSelect }) => (
  <div data-testid="client-siblings-strip">
    <MicroLabel>Vos véhicules · {siblings.length + 1}</MicroLabel>
    <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
      {[
        { ...current, isCurrent: true },
        ...siblings.map((s) => ({
          id: s.id,
          name: s.vehicle_name,
          image: s.vehicle_image,
          orderNumber: s.order_number,
          progress: s.progress,
        })),
      ].map((v) => (
        <button
          key={v.id}
          onClick={() => !v.isCurrent && onSelect(v.id)}
          data-testid={`client-sibling-${v.id}`}
          className={[
            "shrink-0 w-[200px] rounded-2xl border overflow-hidden text-left snap-start transition-all",
            v.isCurrent
              ? "border-[#C8FF2F] bg-[#F3FCE1] ring-1 ring-[#C8FF2F] cursor-default"
              : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#DCE1E7] active:scale-[0.99]",
          ].join(" ")}
        >
          {v.image && (
            <div
              className="h-[88px] bg-cover bg-center"
              style={{ backgroundImage: `url(${v.image})` }}
              aria-hidden
            />
          )}
          <div className="p-3">
            <p className="text-[13px] font-semibold text-[#0A0A0A] dark:text-white truncate">
              {v.name}
            </p>
            <p className="text-[10.5px] text-[#6B7280] dark:text-[#A3A3A3] mt-0.5 tabular-nums">
              {v.orderNumber}
            </p>
            {typeof v.progress === "number" && (
              <div className="mt-2 h-1 rounded-full bg-[#EEF2F5] dark:bg-[#262626] overflow-hidden">
                <div
                  className={v.isCurrent ? "h-full bg-[#0A0A0A] dark:bg-white" : "h-full bg-[#0A0A0A] dark:bg-white opacity-40"}
                  style={{ width: `${Math.max(4, Math.min(100, v.progress))}%` }}
                />
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  </div>
);

const LastCompletedCard = ({ step }) => (
  <Card className="p-5 flex items-center gap-4" data-testid="client-last-completed">
    <div className="h-10 w-10 rounded-2xl bg-[#F3FCE1] dark:bg-[#1F2A0A] text-[#4a6b0f] dark:text-[#C8FF2F] flex items-center justify-center shrink-0">
      <Check size={16} strokeWidth={2.5} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#4a6b0f] dark:text-[#C8FF2F]">
        Terminé
      </p>
      <p className="mt-0.5 text-[14.5px] font-semibold text-[#0A0A0A] dark:text-white leading-tight">
        {step.label}
      </p>
    </div>
  </Card>
);

const AdvisorCard = ({ advisor, fallbackName, onMessage }) => {
  const name = advisor?.name || fallbackName || "Votre conseiller";
  return (
    <Card
      className="p-5 flex items-center gap-4"
      data-testid="client-advisor-card"
    >
      <Avatar name={name} className="h-11 w-11 !bg-[#0A0A0A] !text-white dark:!bg-white dark:!text-[#0A0A0A] !text-[12px]" />
      <div className="flex-1 min-w-0">
        <p className="text-[10.5px] font-medium text-[#6B7280] dark:text-[#A3A3A3]">
          Votre conseiller
        </p>
        <p className="mt-0.5 text-[14px] font-semibold text-[#0A0A0A] dark:text-white truncate">
          {name}
        </p>
      </div>
      {onMessage && (
        <Button
          variant="dark"
          icon={MessageCircle}
          onClick={onMessage}
          data-testid="client-advisor-message"
        >
          Écrire
        </Button>
      )}
    </Card>
  );
};

const PasswordSetupCard = ({ pwd, setPwd, busy, error, saved, onSubmit, onDismiss }) => (
  <Card className="p-5" data-testid="client-password-card">
    {saved ? (
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-[#F3FCE1] dark:bg-[#1F2A0A] text-[#4a6b0f] dark:text-[#C8FF2F] flex items-center justify-center shrink-0">
          <Check size={16} strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[14px] font-semibold text-[#0A0A0A] dark:text-white">
            Mot de passe enregistré
          </p>
          <p className="text-[12px] text-[#6B7280] dark:text-[#A3A3A3]">
            Vous pourrez désormais vous connecter avec email + mot de passe.
          </p>
        </div>
      </div>
    ) : (
      <>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-[#F3F4F6] dark:bg-[#1A1A1A] text-[#0A0A0A] dark:text-white flex items-center justify-center shrink-0">
              <Lock size={15} strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[14.5px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em]">
                Sécurisez votre compte
              </p>
              <p className="text-[12px] text-[#6B7280] dark:text-[#A3A3A3] mt-0.5">
                Créez un mot de passe pour vous reconnecter plus facilement.
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Ignorer"
            data-testid="client-password-dismiss"
            className="h-7 w-7 rounded-full text-[#9CA3AF] hover:text-[#0A0A0A] dark:hover:text-white flex items-center justify-center shrink-0"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="mt-4">
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Nouveau mot de passe"
            data-testid="client-password-input"
            className="w-full h-11 px-4 rounded-full border border-[#E5E7EB] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] text-[14px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
          />
          {error && (
            <p data-testid="client-password-error" className="mt-2 text-[12px] text-[#B91C1C]">
              {error}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              loading={busy}
              data-testid="client-password-submit"
            >
              Enregistrer
            </Button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="client-password-skip"
              className="h-10 px-4 text-[13px] font-medium text-[#6B7280] hover:text-[#0A0A0A] dark:text-[#A3A3A3] dark:hover:text-white"
            >
              Ignorer pour l&apos;instant
            </button>
          </div>
          <p className="mt-2 text-[11px] text-[#9CA3AF] dark:text-[#525252]">
            Minimum 8 caractères · au moins 1 lettre et 1 chiffre.
          </p>
        </form>
      </>
    )}
  </Card>
);

export default ClientWelcome;
